import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap";

function useFonts() {
  useEffect(() => {
    if (document.querySelector('link[href*="Instrument+Serif"]')) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = FONTS_URL;
    document.head.appendChild(l);
  }, []);
}

const T = {
  g700: "#155c36", g600: "#1b7a47", g500: "#1e9254", g400: "#28b566",
  g200: "#a7d9bc", g100: "#d6f0e3", g50: "#eef8f3",
  n900: "#0f1117", n800: "#1c2130", n700: "#2d3549", n600: "#4a5268",
  n400: "#8a93a8", n300: "#b0b8cc", n200: "#d1d5e0", n150: "#e4e7ef",
  n100: "#eef0f5", n50: "#f7f8fa", white: "#ffffff",
  amber: "#b45309", amberBg: "#fffbeb", amberBorder: "#fde68a",
  serif: "'Instrument Serif', Georgia, serif",
  sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

const Icon = ({ name, size = 20, color = "currentColor", strokeWidth = 1.6 }) => {
  const paths = {
    ArrowLeft:     <><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></>,
    ArrowRight:    <><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></>,
    Search:        <><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></>,
    Calendar:      <><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></>,
    CreditCard:    <><rect width="20" height="14" x="2" y="5" rx="2"/><path d="M2 10h20"/></>,
    Star:          <><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></>,
    Shield:        <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
    MessageCircle: <><path d="m3 21 1.9-5.7a8.5 8.5 0 1 1 3.8 3.8z"/></>,
    CheckCircle:   <><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></>,
    MapPin:        <><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/></>,
    Clock:         <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
    Award:         <><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/></>,
    Briefcase:     <><rect width="20" height="14" x="2" y="7" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></>,
    Phone:         <><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.5a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.34 3h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 10.09a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></>,
    Users:         <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
    Zap:           <><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></>,
    ThumbsUp:      <><path d="M7 10v12"/><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88z"/></>,
    Lock:          <><rect width="18" height="11" x="3" y="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>,
    ChevronDown:   <><path d="m6 9 6 6 6-6"/></>,
    ChevronUp:     <><path d="m18 15-6-6-6 6"/></>,
    Info:          <><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></>,
    Sparkles:      <><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3z"/></>,
    Home:          <><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>,
  };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

/* ── Tab toggle ── */
function TabBar({ active, onChange }) {
  return (
    <div style={{ display: "inline-flex", background: T.n100, borderRadius: 12, padding: 4, gap: 4 }}>
      {["client", "pro"].map(tab => (
        <button key={tab} onClick={() => onChange(tab)} style={{
          padding: "10px 28px", borderRadius: 9, border: "none", cursor: "pointer",
          fontFamily: T.sans, fontSize: 14, fontWeight: 600, letterSpacing: "0.01em",
          background: active === tab ? T.white : "transparent",
          color: active === tab ? T.n900 : T.n400,
          boxShadow: active === tab ? "0 1px 4px rgba(15,17,23,.08)" : "none",
          transition: "all .2s",
        }}>
          {tab === "client" ? "I need a service" : "I offer services"}
        </button>
      ))}
    </div>
  );
}

/* ── Step card ── */
function StepCard({ num, icon, title, desc, detail, accent = false }) {
  return (
    <div style={{
      display: "flex", gap: 20, padding: "28px 0",
      borderBottom: `1px solid ${T.n100}`,
    }}>
      {/* Number + connector */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, width: 48 }}>
        <div style={{
          width: 48, height: 48, borderRadius: 14,
          background: accent ? T.g500 : T.g50,
          border: `1.5px solid ${accent ? T.g500 : T.g100}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          <Icon name={icon} size={20} color={accent ? T.white : T.g500} strokeWidth={1.6} />
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, paddingTop: 4 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>
          Step {num}
        </div>
        <h3 style={{ fontFamily: T.serif, fontSize: "clamp(20px,3vw,24px)", fontWeight: 400, color: T.n900, margin: "0 0 10px", letterSpacing: "-0.02em", lineHeight: 1.25 }}>
          {title}
        </h3>
        <p style={{ fontSize: 15, color: T.n600, lineHeight: 1.75, margin: "0 0 14px" }}>{desc}</p>
        {detail && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {detail.map((d, i) => (
              <span key={i} style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                fontSize: 13, color: T.g700, background: T.g50,
                border: `1px solid ${T.g100}`, borderRadius: 99,
                padding: "4px 12px", fontWeight: 500,
              }}>
                <Icon name="CheckCircle" size={13} color={T.g500} strokeWidth={2} />
                {d}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Feature pill row ── */
function FeatureRow({ icon, title, desc }) {
  return (
    <div style={{ display: "flex", gap: 16, padding: "20px 0", borderBottom: `1px solid ${T.n100}` }}>
      <div style={{ width: 40, height: 40, borderRadius: 11, background: T.g50, border: `1px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon name={icon} size={18} color={T.g500} strokeWidth={1.6} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: T.n800, marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 14, color: T.n600, lineHeight: 1.65 }}>{desc}</div>
      </div>
    </div>
  );
}

/* ── FAQ item ── */
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${T.n100}` }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "18px 0", background: "none", border: "none", cursor: "pointer",
        fontFamily: T.sans, textAlign: "left", gap: 16,
      }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: T.n800, lineHeight: 1.4 }}>{q}</span>
        <span style={{ flexShrink: 0, color: T.n400 }}>
          <Icon name={open ? "ChevronUp" : "ChevronDown"} size={18} strokeWidth={2} />
        </span>
      </button>
      {open && (
        <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.75, margin: "0 0 18px", paddingRight: 32 }}>{a}</p>
      )}
    </div>
  );
}

/* ── Guarantee badge ── */
function GuaranteeBadge({ icon, label, sub }) {
  return (
    <div style={{
      flex: "1 1 160px", padding: "20px", borderRadius: 14,
      border: `1px solid ${T.g100}`, background: T.g50,
      display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 10,
    }}>
      <div style={{ width: 38, height: 38, borderRadius: 10, background: T.white, border: `1px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon name={icon} size={18} color={T.g500} strokeWidth={1.6} />
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.g700, marginBottom: 3 }}>{label}</div>
        <div style={{ fontSize: 12, color: T.g600, lineHeight: 1.55 }}>{sub}</div>
      </div>
    </div>
  );
}

/* ══ Main page ══ */
export default function HowItWorksPage() {
  useFonts();
  const navigate = useNavigate();
  const [tab, setTab] = useState("client");

  const clientSteps = [
    {
      num: "01", icon: "Search", title: "Search for a service",
      desc: "Enter the type of service you need and your location. JiraniApp instantly surfaces verified local professionals with real ratings, transparent pricing, and available slots. No endless scrolling through unverified listings.",
      detail: ["Filter by price", "Filter by rating", "Filter by distance", "Filter by availability"],
    },
    {
      num: "02", icon: "Users", title: "Browse and choose your JiraniPro",
      desc: "Every professional on JiraniApp has gone through identity verification and background checks. Review their full profile — services offered, experience, portfolio photos, client reviews, and response rate — before making a decision.",
      detail: ["Verified identity", "Background checked", "Real client reviews", "Response rate shown"],
    },
    {
      num: "03", icon: "Calendar", title: "Book your appointment",
      desc: "Pick a date and time that works for you. Add your service address, any access instructions, and special requirements. Your JiraniPro gets notified immediately and confirms the booking.",
      detail: ["Live availability", "Instant confirmation", "Reminder notifications", "Easy rescheduling"],
      accent: true,
    },
    {
      num: "04", icon: "CreditCard", title: "Pay securely through the platform",
      desc: "Pay via M-Pesa or other supported methods — all through JiraniApp. Your payment is held securely and only released to the JiraniPro once the job is done to your satisfaction. No cash, no awkward conversations.",
      detail: ["M-Pesa supported", "Payment held securely", "Released on completion", "Full receipts"],
    },
    {
      num: "05", icon: "Home", title: "Get the service done",
      desc: "Track your JiraniPro's status in real time, communicate through in-app messaging if needed, and follow the job from arrival to completion. Everything is documented on the platform.",
      detail: ["Real-time tracking", "In-app messaging", "Job status updates", "Photo documentation"],
    },
    {
      num: "06", icon: "Star", title: "Review and rate your experience",
      desc: "After the job is complete, leave an honest rating and review. Your feedback helps other clients make informed decisions and helps great JiraniPros build their reputation. If anything went wrong, our dispute process is right here.",
      detail: ["Star rating", "Written review", "Photo upload", "Dispute option"],
    },
  ];

  const proSteps = [
    {
      num: "01", icon: "Phone", title: "Sign up and create your profile",
      desc: "Register with your phone number and build a detailed professional profile. List every service you offer, set your pricing, upload portfolio photos, and write a short bio that helps clients understand who you are and why they should choose you.",
      detail: ["Phone-based signup", "Service listing", "Portfolio photos", "Custom pricing"],
    },
    {
      num: "02", icon: "Award", title: "Complete identity verification",
      desc: "JiraniApp verifies every professional before they go live. This protects clients and it protects you too — being a verified JiraniPro is a mark of trust that makes you stand out. The process is straightforward and handled entirely in-app.",
      detail: ["ID verification", "Background check", "Profile reviewed", "Badge awarded"],
      accent: true,
    },
    {
      num: "03", icon: "Calendar", title: "Set your availability",
      desc: "Use the availability manager to mark the days and times you are open for bookings. Clients only see slots you have made available, so you are always in control of your schedule. Update it anytime.",
      detail: ["Weekly schedule", "Block off days", "Instant updates", "No double bookings"],
    },
    {
      num: "04", icon: "Zap", title: "Receive and accept bookings",
      desc: "When a client books you, you get an instant notification with full job details — service type, location, time, and any special instructions. Accept or decline from your dashboard. Consistent acceptance builds your visibility on the platform.",
      detail: ["Instant notifications", "Full job details", "One-tap accept", "Client history shown"],
    },
    {
      num: "05", icon: "Briefcase", title: "Deliver great work",
      desc: "Show up, do excellent work, and use the app to mark job milestones. Communicate with clients through in-app messaging if anything changes. Professional, reliable JiraniPros earn more bookings — it compounds over time.",
      detail: ["Job milestone updates", "In-app messaging", "Photo documentation", "On-time tracking"],
    },
    {
      num: "06", icon: "CreditCard", title: "Get paid automatically",
      desc: "Once the job is marked complete and the client confirms, your payout is processed automatically. Track all your earnings, upcoming payouts, and completed jobs in the Earnings dashboard. Transparent, on time, every time.",
      detail: ["Auto payout", "Earnings dashboard", "Payout history", "M-Pesa supported"],
    },
  ];

  const clientFaqs = [
    { q: "How are JiraniPros screened?", a: "Every professional who joins JiraniApp goes through identity verification and a background check before their profile goes live. We review their submitted credentials and service history. Professionals who receive reports or low ratings are reviewed by our Trust and Safety team." },
    { q: "What if the professional does not show up?", a: "If your JiraniPro cancels last minute or does not arrive, you will not be charged. You can rebook with another professional immediately. If a no-show affects you and the issue is disputed, our Trust and Safety team will review the case and issue a refund where applicable under the Jirani Guarantee." },
    { q: "Can I cancel or reschedule a booking?", a: "Yes. Cancellations made at least 24 hours before the scheduled time are free of charge. Late cancellations may incur a small fee, which is clearly shown when you cancel. Rescheduling is available in the Bookings section and is generally free with sufficient notice." },
    { q: "What if I am not happy with the work?", a: "Raise a dispute within 48 hours of the completed job through the Help and Support page. Describe what went wrong and attach any relevant photos. Our Trust and Safety team reviews every dispute and will work with both you and the JiraniPro to find a fair resolution, including a partial or full refund where warranted." },
    { q: "How does payment work?", a: "You pay through JiraniApp using M-Pesa or other supported methods. Your payment is held securely and only released to the JiraniPro once the job is marked complete. You will always receive a receipt and a full record of the transaction in your account." },
    { q: "Is my personal information shared with professionals?", a: "Only the information necessary to carry out your booking is shared with your JiraniPro — your name, service address, and booking details. Your phone number and other personal data are protected. JiraniPros are bound by our Terms of Service and are prohibited from using your information outside of fulfilling your booking." },
  ];

  const proFaqs = [
    { q: "Is there a cost to join JiraniApp?", a: "Creating a JiraniPro profile is free. JiraniApp charges a platform fee on completed bookings, which covers payment processing, customer support, and the cost of operating the platform. The fee is clearly disclosed before you accept any job." },
    { q: "How and when do I get paid?", a: "Your payout is processed automatically once a job is marked complete and confirmed. Funds are sent to your registered M-Pesa number. You can track all earnings, upcoming payouts, and completed jobs in your Earnings dashboard." },
    { q: "What happens if a client raises a dispute?", a: "Our Trust and Safety team reviews all disputes fairly. You will be contacted and given the opportunity to share your side, including any messages, photos, or job records. We aim to resolve disputes within 2 business days." },
    { q: "Can I set my own prices?", a: "Yes. You set your own service prices when you build your profile. You can update your pricing at any time from the Manage Services section of your dashboard. Clients see your prices clearly before booking." },
    { q: "What if I need to cancel a booking?", a: "We understand that unexpected things happen. If you need to cancel, do so as early as possible through the app so the client can rebook. Frequent cancellations negatively affect your profile visibility, so we encourage maintaining your availability accurately to avoid conflicts." },
    { q: "How do I improve my ranking and get more bookings?", a: "The most reliable ways to rank higher are maintaining a high rating, accepting bookings consistently, completing jobs on time, and keeping your availability up to date. Clients also search by distance, so having a detailed, complete profile increases your chances of being selected." },
  ];

  const steps = tab === "client" ? clientSteps : proSteps;
  const faqs = tab === "client" ? clientFaqs : proFaqs;

  return (
    <div style={{ fontFamily: T.sans, background: T.white, minHeight: "100vh", color: T.n900 }}>
      <style>{`
        * { box-sizing: border-box; }
        .jrn-link { color: ${T.g600}; text-decoration: none; border-bottom: 1px solid ${T.g200}; transition: border-color .15s, color .15s; }
        .jrn-link:hover { color: ${T.g700}; border-color: ${T.g600}; }
      `}</style>

      {/* ══ NAV ══ */}
      <div style={{ padding: "20px 24px", borderBottom: `1px solid ${T.n100}` }}>
        <button onClick={() => navigate(-1)} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: T.n400, fontSize: 13, fontFamily: T.sans, padding: 0, transition: "color .15s" }}
          onMouseEnter={e => (e.currentTarget.style.color = T.n800)}
          onMouseLeave={e => (e.currentTarget.style.color = T.n400)}>
          <Icon name="ArrowLeft" size={14} strokeWidth={2} />
          Back
        </button>
      </div>

      {/* ══ HERO ══ */}
      <div style={{ padding: "clamp(48px,7vw,88px) 20px clamp(40px,5vw,60px)", borderBottom: `1px solid ${T.n100}`, textAlign: "center" }}>
        <div style={{ maxWidth: 680, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 7, color: T.g600, fontSize: 12, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 20 }}>
            <Icon name="Sparkles" size={13} color={T.g500} strokeWidth={2} />
            How JiraniApp works
          </div>
          <h1 style={{ fontFamily: T.serif, fontSize: "clamp(40px,7vw,64px)", fontWeight: 400, margin: "0 0 18px", lineHeight: 1.05, color: T.n900, letterSpacing: "-0.03em" }}>
            Home services,{" "}
            <em style={{ fontStyle: "italic", color: T.g600 }}>simplified</em>
          </h1>
          <p style={{ fontSize: "clamp(15px,2vw,17px)", color: T.n600, lineHeight: 1.75, margin: "0 auto 36px", maxWidth: 520 }}>
            JiraniApp connects you with trusted, verified home service professionals in your neighbourhood.
            Whether you need a plumber, cleaner, electrician, or gardener, booking takes minutes.
          </p>
          <TabBar active={tab} onChange={setTab} />
        </div>
      </div>

      {/* ══ CONTENT ══ */}
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 20px clamp(80px,10vw,120px)" }}>

        {/* ── Steps ── */}
        <div style={{ paddingTop: "clamp(40px,6vw,64px)" }}>
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase" }}>
              {tab === "client" ? "For clients" : "For JiraniPros"}
            </span>
          </div>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4vw,34px)", fontWeight: 400, color: T.n900, margin: "0 0 4px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            {tab === "client"
              ? <>From search to <em style={{ fontStyle: "italic", color: T.g600 }}>done</em></>
              : <>From signup to <em style={{ fontStyle: "italic", color: T.g600 }}>paid</em></>
            }
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 0", lineHeight: 1.6 }}>
            {tab === "client"
              ? "Six straightforward steps that put you in full control."
              : "Everything you need to start earning on JiraniApp."}
          </p>
          <div style={{ marginTop: 8 }}>
            {steps.map(s => <StepCard key={s.num} {...s} />)}
          </div>
        </div>

        {/* ── Jirani Guarantee ── */}
        <div style={{ margin: "clamp(48px,7vw,72px) 0 0", padding: "clamp(28px,5vw,40px)", borderRadius: 20, background: T.g50, border: `1px solid ${T.g100}` }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 7, color: T.g600, fontSize: 11, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 14 }}>
            <Icon name="Shield" size={13} color={T.g500} strokeWidth={2} />
            Jirani Guarantee
          </div>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(24px,4vw,32px)", fontWeight: 400, color: T.n900, margin: "0 0 10px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            Your booking is <em style={{ fontStyle: "italic", color: T.g600 }}>protected</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n600, lineHeight: 1.75, margin: "0 0 28px" }}>
            Every booking made through JiraniApp is backed by our Guarantee. If something goes wrong,
            we are here to make it right. No runaround, no finger pointing.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            <GuaranteeBadge icon="CheckCircle" label="Verified professionals" sub="Every JiraniPro is ID-verified before going live." />
            <GuaranteeBadge icon="Lock" label="Secure payments" sub="Your payment is held until the job is done." />
            <GuaranteeBadge icon="MessageCircle" label="Dispute resolution" sub="Our team reviews and resolves issues within 48 hours." />
            <GuaranteeBadge icon="ThumbsUp" label="Satisfaction focus" sub="Refunds issued where service was not delivered." />
          </div>
        </div>

        {/* ── What makes us different ── */}
        <div style={{ marginTop: "clamp(48px,7vw,72px)" }}>
          <div style={{ marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase" }}>Why JiraniApp</span>
          </div>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(24px,4vw,32px)", fontWeight: 400, color: T.n900, margin: "0 0 4px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            Built for trust, <em style={{ fontStyle: "italic", color: T.g600 }}>built for Kenya</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 8px", lineHeight: 1.65 }}>
            JiraniApp is designed around how home services actually work here.
          </p>
          <div>
            <FeatureRow icon="MapPin" title="Hyperlocal matching" desc="We surface professionals in your immediate neighbourhood, not just your city. Closer means faster, more reliable, and easier to verify." />
            <FeatureRow icon="Phone" title="Phone-first experience" desc="No email required. Sign up and book entirely through your phone number. M-Pesa payments mean no card details, no friction." />
            <FeatureRow icon="Clock" title="Real-time availability" desc="JiraniPros set their own live availability. What you see is accurate. No booking a slot only to be told the pro is unavailable." />
            <FeatureRow icon="Star" title="Genuine reviews only" desc="Reviews are tied to completed, verified bookings. No anonymous ratings, no fake reviews. What you read is what real clients experienced." />
            <FeatureRow icon="MessageCircle" title="In-app communication" desc="All messages between clients and professionals stay in JiraniApp. This keeps your contact details private and creates a clear record if anything is disputed." />
            <FeatureRow icon="Award" title="Professionals who are invested" desc="JiraniPros build their reputation on the platform over time. That means they are incentivised to do great work, not just take a job and disappear." />
          </div>
        </div>

        {/* ── FAQ ── */}
        <div style={{ marginTop: "clamp(48px,7vw,72px)" }}>
          <div style={{ marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase" }}>
              {tab === "client" ? "Client questions" : "JiraniPro questions"}
            </span>
          </div>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(24px,4vw,32px)", fontWeight: 400, color: T.n900, margin: "0 0 4px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            Common <em style={{ fontStyle: "italic", color: T.g600 }}>questions</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 8px", lineHeight: 1.65 }}>
            Straight answers to what people ask most.
          </p>
          <div style={{ marginTop: 8 }}>
            {faqs.map((f, i) => <FaqItem key={i} {...f} />)}
          </div>
        </div>

        {/* ── CTA ── */}
        <div style={{ marginTop: "clamp(48px,7vw,72px)", padding: "clamp(32px,5vw,48px)", borderRadius: 20, border: `1px solid ${T.n150}`, textAlign: "center", background: T.n50 }}>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 12px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            {tab === "client"
              ? <>Ready to find your <em style={{ fontStyle: "italic", color: T.g600 }}>JiraniPro?</em></>
              : <>Ready to start <em style={{ fontStyle: "italic", color: T.g600 }}>earning?</em></>
            }
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 28px", lineHeight: 1.65 }}>
            {tab === "client"
              ? "Browse verified professionals in your area and book in minutes."
              : "Create your free profile and start receiving bookings today."}
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button
              onClick={() => navigate(tab === "client" ? "/search" : "/pro-onboarding")}
              style={{
                padding: "13px 28px", background: T.g500, color: T.white,
                border: "none", borderRadius: 11, fontSize: 14, fontWeight: 600,
                fontFamily: T.sans, cursor: "pointer", letterSpacing: "0.01em",
                display: "inline-flex", alignItems: "center", gap: 8,
              }}
              onMouseEnter={e => (e.currentTarget.style.background = T.g600)}
              onMouseLeave={e => (e.currentTarget.style.background = T.g500)}>
              {tab === "client" ? "Find a professional" : "Join as a JiraniPro"}
              <Icon name="ArrowRight" size={15} color={T.white} strokeWidth={2} />
            </button>
            <button
              onClick={() => navigate("/help")}
              style={{
                padding: "13px 28px", background: "transparent", color: T.n700,
                border: `1.5px solid ${T.n200}`, borderRadius: 11, fontSize: 14, fontWeight: 600,
                fontFamily: T.sans, cursor: "pointer", letterSpacing: "0.01em",
              }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = T.n400)}
              onMouseLeave={e => (e.currentTarget.style.borderColor = T.n200)}>
              Visit Help and Support
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}